<template>
    <div class="relative min-h-screen w-full flex bg-gradient-to-br from-gray-100 to-blue-50">
        <div class="container mx-auto px-4 sm:px-8 py-8">
            <!-- Page Title -->
            <div>
                <h1 class="text-2xl font-bold text-gray-800 flex items-center space-x-2">
                    <span>🏫</span>
                    <span>FCHS - AA Facility Overview</span>
                </h1>
            </div>

            <!-- Summary Cards -->
            <div v-if="facilityAreas.length" class="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div v-for="(area, index) in facilityAreas" :key="index"
                    class="bg-white p-5 shadow-sm border border-gray-100 hover:shadow-md transition-all">
                    <p class="text-sm text-gray-500 mb-1 truncate">{{ area.title }}</p>
                    <p class="text-2xl font-semibold">
                        {{ formatNumber(area.value) }} <span class="text-base text-gray-600">{{ area.unit }}</span>
                    </p>
                </div>
            </div>

            <div class="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                <!-- <ClassroomLab :data="getClassroomLabData" /> -->
                <Top5ModuleChart :data="getTop5Modules" />
                <FacilityArea v-if="facilityAreas.length" :data="facilityAreas" />
            </div>

            <div class="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <LabsByType :data="labsJsonData" />
                <LabsByCapacity :data="labsJsonData" />
                <LabsByFloor :data="labsJsonData" />
                <VendingMachineByVendor :data="vendingMachinesJsonData" />
                <VendingMachineByFloor :data="vendingMachinesJsonData" />
                <VendingMachineByType :data="vendingMachinesJsonData" />
                <div class="col-span-1 sm:col-span-2 md:col-span-1">
                    <PalmAndTreesByCount :data="palmAndTreesJsonData" />
                </div>
                <PalmAndTreesByArea class="col-span-1 sm:col-span-2 md:col-span-1 lg:col-span-2"
                    :data="palmAndTreesJsonData" />
            </div>

            <!-- Facility Modules Grid -->
            <div class="mt-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                <div v-for="module in facilityModules" :key="module.key" @click="openFacilityPopup(module)"
                    class="cursor-pointer border p-4 shadow-sm hover:shadow-md transition bg-white flex items-center justify-center flex-col text-center">
                    <div class="text-3xl mb-2">{{ module.icon }}</div>

                    <div class="text-sm font-medium text-gray-800">{{ module.label }}</div>

                    <div v-if="module.count"
                        class="text-xs mt-1 px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800">
                        {{ module.count }} {{ pluralize(module.unit, module.count) }}
                    </div>
                </div>
            </div>

            <!-- Class Timing Distribution -->
            <div class="mt-8" v-if="classTimingDistribution">
                <ClassTimingDistributionChart :timingData="classTimingDistribution" :powerBiColors="powerBiColors" />
            </div>
            <div class="mt-8" v-else-if="facultySummaryLoading">
                <div class="bg-white p-6 rounded-lg shadow-lg">
                    <div class="flex justify-center items-center h-64">
                        <base-spinner type="spin" class-list="h-12"></base-spinner>
                    </div>
                </div>
            </div>

            <!-- Day Distribution -->
            <div class="mt-8" v-if="dayDistribution">
                <DayDistributionChart :data="dayDistribution" :powerBiColors="powerBiColors" />
            </div>
            <div class="mt-8" v-else-if="facultySummaryLoading">
                <div class="bg-white p-6 rounded-lg shadow-lg">
                    <div class="flex justify-center items-center h-64">
                        <base-spinner type="spin" class-list="h-12"></base-spinner>
                    </div>
                </div>
            </div>

            <!-- Classroom Table -->
            <div class="mt-8 bg-white p-6 shadow overflow-x-auto">
                <div class="flex justify-between items-center mb-6">
                    <h2 class="text-lg font-semibold text-blue-700 mb-4">📘 Classrooms</h2>
                    <button v-if="hasSuperPermission('create-facility-room')" @click="addFacility('class_room')"
                        class="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl shadow-md hover:shadow-lg hover:from-green-600 hover:to-green-700 transition duration-200 focus:outline-none focus:ring-2 focus:ring-green-400 text-sm">
                        <font-awesome-icon :icon="['fas', 'plus']" />
                        Add Room
                    </button>
                </div>

                <table class="min-w-full divide-y divide-gray-200 text-sm">
                    <thead class="bg-gray-100 text-gray-600">
                        <tr>
                            <th class="px-4 py-2 text-left">Room</th>
                            <th class="px-4 py-2 text-center">Current Capacity</th>
                            <th class="px-4 py-2 text-center">Maximum Capacity</th>
                            <th class="px-4 py-2 text-center">Percentage</th>
                            <th v-if="hasSuperPermission('create-facility-room')" class="px-4 py-2 text-center">Action
                            </th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-100 text-gray-700">
                        <tr v-for="(room, index) in classroomData" :key="index" class="hover:bg-gray-50">
                            <td class="px-4 py-2 font-medium">{{ room.title }}</td>
                            <td class="px-4 py-2 text-center">{{ room.no_of_seats || '-' }}</td>
                            <td class="px-4 py-2 text-center">{{ room.max_seats || '-' }}</td>
                            <td class="px-4 py-2 text-center">{{ getPercentage(room.no_of_seats, room.max_seats) }}
                            </td>
                            <td v-if="hasSuperPermission('create-facility-room')" class="text-center">
                                <button @click="editFacility(room)" class="text-blue-600 hover:text-blue-800">
                                    <font-awesome-icon :icon="['fas', 'pen-to-square']" />
                                </button>
                            </td>
                        </tr>
                    </tbody>
                    <tfoot class="bg-gray-50 font-semibold">
                        <tr>
                            <td class="px-4 py-2 text-right">Total</td>
                            <td class="px-4 py-2 text-center">{{ classroomTotal }}</td>
                            <td class="px-4 py-2 text-center">{{ classroomMaxTotal }}</td>
                            <td></td>
                            <td v-if="hasSuperPermission('create-facility-room')"></td>
                        </tr>
                    </tfoot>
                </table>
            </div>

            <!-- Labs Table -->
            <div class="mt-8 bg-white p-6 shadow overflow-x-auto">
                <div class="flex justify-between items-center mb-6">
                    <h2 class="text-lg font-semibold text-green-700">🧪 Laboratories</h2>
                    <button v-if="hasSuperPermission('create-facility-room')" @click="addFacility('lab')"
                        class="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl shadow-md hover:shadow-lg hover:from-green-600 hover:to-green-700 transition duration-200 focus:outline-none focus:ring-2 focus:ring-green-400 text-sm">
                        <font-awesome-icon :icon="['fas', 'plus']" />
                        Add Lab
                    </button>
                </div>
                <table class="min-w-full divide-y divide-gray-200 text-sm">
                    <thead class="bg-gray-100 text-gray-600">
                        <tr>
                            <th class="px-4 py-2 text-left">Lab</th>
                            <th class="px-4 py-2 text-center">Current Capacity</th>
                            <th class="px-4 py-2 text-center">Maximum Capacity</th>
                            <th class="px-4 py-2 text-center">Percentage</th>
                            <th class="px-4 py-2 text-left">Remarks</th>
                            <th class="px-4 py-2 text-left">Action</th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-100 text-gray-700">
                        <tr v-for="(lab, index) in labData" :key="index" class="hover:bg-gray-50">
                            <td class="px-4 py-2 font-medium">{{ lab.title }}</td>
                            <td class="px-4 py-2 text-center">{{ lab.no_of_seats }}</td>
                            <td class="px-4 py-2 text-center">{{ lab.max_seats }}</td>
                            <td class="px-4 py-2 text-center">{{ getPercentage(lab.no_of_seats, lab.max_seats) }}
                            </td>
                            <td class="px-4 py-2">{{ lab.remarks }}</td>
                            <td v-if="hasSuperPermission('create-facility-room')" class="text-center">
                                <button @click="editFacility(lab)" class="text-blue-600 hover:text-blue-800">
                                    <font-awesome-icon :icon="['fas', 'pen-to-square']" />
                                </button>
                            </td>
                        </tr>
                    </tbody>
                    <tfoot class="bg-gray-50 font-semibold">
                        <tr>
                            <td class="px-4 py-2 text-right">Total</td>
                            <td class="px-4 py-2 text-center">{{ labTotal }}</td>
                            <td class="px-4 py-2 text-center">{{ labMaxTotal }}</td>
                            <td></td>
                            <td></td>
                            <td v-if="hasSuperPermission('create-facility-room')"></td>
                        </tr>
                    </tfoot>
                </table>
            </div>

            <!-- Modal for Add Idea -->
            <transition name="fade">
                <div v-if="showForm"
                    class="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-start justify-center pt-24 px-4">
                    <div class="bg-white w-full max-w-2xl rounded-3xl shadow-2xl p-8 relative border border-gray-300">
                        <!-- Close Button -->
                        <button class="absolute top-5 right-5 text-gray-500 hover:text-red-600 text-xl"
                            @click="cancelSubmit">
                            <font-awesome-icon :icon="['fas', 'times']" />
                        </button>

                        <!-- Form Title -->
                        <h2 class="text-xl font-semibold text-gray-800 flex items-center mb-4">
                            <span class="mr-2">🏢</span>{{ formTitle }}
                        </h2>

                        <form @submit.prevent="handleSubmit" class="grid grid-cols-1 md:grid-cols-2 gap-4">

                            <!-- Entity -->
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">Entity</label>
                                <select v-model="form.entity"
                                    class="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring focus:ring-indigo-200"
                                    required>
                                    <option value="" disabled>Select an Entity</option>
                                    <option>FCHS</option>
                                    <option>ADPOLY</option>
                                    <option>ATS</option>
                                    <option>ADVETI</option>
                                </select>
                            </div>

                            <!-- Campus -->
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">Campus</label>
                                <input v-model="form.campus" type="text"
                                    class="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring focus:ring-indigo-200"
                                    required />
                            </div>

                            <!-- Title -->
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">Title</label>
                                <input v-model="form.title" type="text"
                                    class="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring focus:ring-indigo-200"
                                    required />
                            </div>

                            <!-- No. of Seats -->
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">Current Capacity</label>
                                <input v-model.number="form.no_of_seats" type="number" min="0"
                                    class="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring focus:ring-indigo-200"
                                    required />
                            </div>

                            <!-- Max Seats -->
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">Maximum Capacity</label>
                                <input v-model.number="form.max_seats" type="number" min="0"
                                    class="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring focus:ring-indigo-200"
                                    required />
                            </div>

                            <!-- Remarks -->
                            <div v-if="form.room_type == 'lab'" class="md:col-span-2">
                                <label class="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                                <textarea v-model="form.remarks" rows="3"
                                    class="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring focus:ring-indigo-200"></textarea>
                            </div>

                            <!-- Submit -->
                            <div class="md:col-span-2 text-right flex justify-end gap-3">
                                <button type="button" @click="cancelSubmit"
                                    class="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition">
                                    Cancel
                                </button>
                                <button type="submit"
                                    class="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition">
                                    Submit
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </transition>

        </div>

        <transition name="fade" mode="out-in">
            <base-full-modal v-if="activeModule" :title="activeModule.label" height="" @close="closeModal">
                <component :is="getTableComponent(activeModule.key)" />
            </base-full-modal>
        </transition>

        <div class="h-full flex justify-center items-center" v-if="isLoading">
            <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
        </div>
    </div>
</template>

<script>
import { defineAsyncComponent } from "vue";
import gql from 'graphql-tag';
import { formatNumberInternational, percentage } from "../../helpers/utils";
import ClassroomLab from "./chart/ClassroomLab.vue";
import FacilityArea from "./chart/FacilityArea.vue";
import Top5ModuleChart from "./chart/Top5ModuleChart.vue";
import LabsByType from "./chart/LabsByType.vue";
import LabsByCapacity from "./chart/LabsByCapacity.vue";
import LabsByFloor from "./chart/LabsByFloor.vue";
import VendingMachineByVendor from "./chart/VendingMachineByVendor.vue";
import VendingMachineByFloor from "./chart/VendingMachineByFloor.vue";
import VendingMachineByType from "./chart/VendingMachineByType.vue";
import PalmAndTreesByCount from "./chart/PalmAndTreesByCount.vue";
import PalmAndTreesByArea from "./chart/PalmAndTreesByArea.vue";
import ClassTimingDistributionChart from "../fchs/al-ain/campus/people/charts/ClassTimingDistributionChart.vue";
import DayDistributionChart from "../fchs/al-ain/campus/people/charts/DayDistributionChart.vue";

import classroomJsonData from "./json/classrooms.json";
import labsJsonData from "./json/labs.json";
import vendingMachinesJsonData from "./json/VendingMachines.json";
import palmAndTreesJsonData from "./json/PalmAndTrees.json";

const FACILITY_DETAILS_QUERY = gql`
  query FacilityDetails($entity: String, $campus: String) {
    facilityDetails(entity: $entity, campus: $campus) {
      error
      error_code
      message
      data {
        facilityAreas {
            title
            value
            unit
        }
        facilityRooms {
            id
            entity
            campus
            room_type
            title
            no_of_seats
            max_seats
            remarks
        }
      }
    }
  }
`;

const FACULTY_SUMMARY_QUERY = gql`
  query GetFacultySummary($entity: String, $campus: String, $faculty_type: String, $program: String, $term_code: String) {
    facultySummary(entity: $entity, campus: $campus, faculty_type: $faculty_type, program: $program, term_code: $term_code) {
      error
      error_code
      message
      data {
        class_timing_distribution {
          peak_vs_non_peak {
            category
            class_count
            percentage
          }
          time_of_day {
            category
            class_count
            percentage
          }
          hourly_distribution {
            hour
            class_count
            label
          }
          total_classes
          summary {
            peak_percentage
            morning_percentage
            afternoon_percentage
            evening_percentage
          }
        }
        day_distribution {
          days {
            day_code
            day_name
            class_count
            percentage
          }
          total_classes
          summary {
            weekday_classes
            weekend_classes
            most_busy_day {
              day_code
              day_name
              class_count
              percentage
            }
            least_busy_day {
              day_code
              day_name
              class_count
              percentage
            }
          }
        }
      }
    }
  }
`;

const CREATE_FACILITY_ROOM_MUTATION = gql`
  mutation CreateFacilityRoom($id:ID, $input: FacilityRoomInput!) {
    createFacilityRoom(id: $id, input: $input) {
      error
      error_code
      message
    }
  }
`;

export default {
    name: "FacilityPage",
    components: {
        ClassroomLab,
        FacilityArea,
        Top5ModuleChart,
        LabsByType,
        LabsByCapacity,
        LabsByFloor,
        VendingMachineByVendor,
        VendingMachineByFloor,
        VendingMachineByType,
        PalmAndTreesByCount,
        PalmAndTreesByArea,
        ClassTimingDistributionChart,
        DayDistributionChart,
    },
    data() {
        return {
            isLoading: false,
            filters: {
                entity: "FCHS",
                campus: "FCHS-ALAIN",
            },
            facilityAreas: [],
            facilityRooms: [],
            showForm: false,
            formTitle: "",
            form: {
                room_type: "",
                entity: "FCHS",
                campus: "FCHS-ALAIN",
                title: "",
                no_of_seats: null,
                max_seats: null,
                remarks: ""
            },
            selectedId: null,
            facilityModules: [
                { key: 'RoomsInBuildingModule', label: 'Rooms in Building', icon: '🏢', count: 2, unit: 'floor' },
                { key: 'ClassroomModule', label: 'Classrooms', icon: '🏫', count: 30, unit: 'classroom' },
                { key: 'LabModule', label: 'Labs', icon: '🧪', count: 14, unit: 'lab' },
                { key: 'AcademicOfficeModule', label: 'Academic Offices', icon: '📚', count: 54, unit: 'office' },
                { key: 'AdminOfficeModule', label: 'Admin Offices', icon: '🏛️', count: 26, unit: 'office' },
                { key: 'AuditoriumModule', label: 'Auditorium', icon: '🎭', count: 1, unit: 'auditorium' },
                { key: 'MeetingRoomModule', label: 'Meeting Rooms', icon: '📅', count: 2, unit: 'meeting room' },
                { key: 'CarParkingModule', label: 'Car Parking', icon: '🚗', count: 302, unit: 'slot' },
                { key: 'GateModule', label: 'Gates', icon: '🚪', count: 3, unit: 'gate' },
                { key: 'ToiletModule', label: 'Toilets', icon: '🚻', count: 41, unit: 'toilet' },
                { key: 'StoreModule', label: 'Stores', icon: '📦', count: 21, unit: 'store' },
                { key: 'ServiceRoomModule', label: 'Service Rooms', icon: '🛠️', count: 39, unit: 'service room' },
                { key: 'SportFacilityModule', label: 'Sport Facilities', icon: '🏟️', count: 4, unit: 'facility' },
                { key: 'CanteenModule', label: 'Canteen', icon: '🍽️', count: 1, unit: 'canteen' },
                { key: 'ClinicModule', label: 'Clinic', icon: '🏥', count: 1, unit: 'clinic' },
                { key: 'PrayerRoomModule', label: 'Prayer Room', icon: '🕌', count: 5, unit: 'prayer room' },
                { key: 'PantryKitchenModule', label: 'Pantries/Kitchen', icon: '🍳', count: 7, unit: 'kitchen' },
                { key: 'VendingMachineModule', label: 'Vending Machines', icon: '🥤', count: 8, unit: 'machine' },
                { key: 'PalmAndTreeModule', label: 'Palm & Trees', icon: '🌴', count: 275, unit: 'plant' },
                { key: 'DocumentModule', label: 'Documents', icon: '📄', count: 0, unit: 'document' },
            ],
            activeModule: null,
            classroomJsonData: [],
            labsJsonData: [],
            vendingMachinesJsonData: [],
            palmAndTreesJsonData: [],
            powerBiColors: [
                "#01B8AA", "#374649", "#FD625E", "#F2C80F", "#5F6B6D",
                "#8AD4EB", "#FE9666", "#A66999", "#3599B8", "#DFBFBF",
                "#95A5A6",
            ],
            facultySummary: null,
            facultySummaryLoading: false,
        };
    },
    created() {
        this.classroomJsonData = classroomJsonData;
        this.labsJsonData = labsJsonData;
        this.vendingMachinesJsonData = vendingMachinesJsonData;
        this.palmAndTreesJsonData = palmAndTreesJsonData;
        this.loadData();
        this.fetchFacultySummary();
    },
    computed: {
        getTableComponent() {
            return (key) => {
                return defineAsyncComponent(() =>
                    import(`./modules/${key}.vue`)
                );
            };
        },
        classroomData() {
            return this.facilityRooms.filter(room => room.room_type === 'class_room');
        },
        classroomTotal() {
            return this.classroomData.reduce((sum, room) => sum + (room.no_of_seats || 0), 0);
        },
        classroomMaxTotal() {
            return this.classroomData.reduce((sum, room) => sum + (room.max_seats || 0), 0);
        },
        labData() {
            return this.facilityRooms.filter(room => room.room_type === 'lab');
        },
        labTotal() {
            return this.labData.reduce((sum, lab) => sum + (lab.no_of_seats || 0), 0);
        },
        labMaxTotal() {
            return this.labData.reduce((sum, lab) => sum + (lab.max_seats || 0), 0);
        },
        getClassroomLabData() {
            return [
                {
                    name: "Classroom Seats",
                    value: this.classroomTotal,
                },
                {
                    name: "Laboratory Seats",
                    value: this.labTotal,
                },
            ];
        },
        getTop5Modules() {
            const topModules = [...this.facilityModules]
                .sort((a, b) => b.count - a.count)
                .slice(0, 5);
            return topModules;
        },
        classTimingDistribution() {
            if (!this.facultySummary || !this.facultySummary.class_timing_distribution) {
                return null;
            }
            return this.facultySummary.class_timing_distribution;
        },
        dayDistribution() {
            if (!this.facultySummary || !this.facultySummary.day_distribution) {
                return null;
            }
            return this.facultySummary.day_distribution;
        },
    },
    methods: {
        async loadData() {
            this.isLoading = true;
            try {
                const response = await this.$apollo.query({
                    query: FACILITY_DETAILS_QUERY,
                    variables: {
                        entity: this.filters.entity,
                        campus: this.filters.campus,
                    },
                    fetchPolicy: 'network-only'
                });

                const facilityDetails = response.data.facilityDetails.data;
                this.facilityAreas = facilityDetails.facilityAreas;
                this.facilityRooms = facilityDetails.facilityRooms;

            } catch (error) {
                console.error("Failed to load students statistics:", error);
            }
            this.isLoading = false;
        },
        async fetchFacultySummary() {
            this.facultySummaryLoading = true;
            try {
                const response = await this.$apollo.query({
                    query: FACULTY_SUMMARY_QUERY,
                    variables: {
                        entity: this.filters.entity,
                        campus: this.filters.campus,
                        faculty_type: 'academic',
                        program: '',
                        term_code: '202610'
                    },
                    fetchPolicy: 'network-only'
                });

                if (response.data.facultySummary && !response.data.facultySummary.error) {
                    this.facultySummary = response.data.facultySummary.data;
                }
            } catch (error) {
                console.error("Failed to fetch faculty summary:", error);
            }
            this.facultySummaryLoading = false;
        },
        formatNumber(val) {
            return formatNumberInternational(val);
        },
        getPercentage(x, y) {
            return percentage(x, y);
        },
        hasSuperPermission(crudName) {
            const permissions = this.$store.getters.permissions;
            const permissionsArray = permissions.split(",");
            if (permissionsArray.includes(crudName)) {
                return true;
            } else {
                return false;
            }
        },
        addFacility(roomType) {
            if (roomType == 'class_room') {
                this.form.room_type = roomType;
                this.formTitle = 'Add Facility Room';
                this.showForm = true;
            } else if (roomType == 'lab') {
                this.form.room_type = roomType;
                this.formTitle = 'Add Facility Lab';
                this.showForm = true;
            } else {
                return;
            }
        },
        editFacility(item) {
            this.selectedId = item.id;
            this.form = {
                entity: item.entity || '',
                campus: item.campus || '',
                room_type: item.room_type || '',
                title: item.title || '',
                no_of_seats: item.no_of_seats || 0,
                max_seats: item.max_seats || 0,
                remarks: item.remarks || '',
            };
            this.showForm = true;
        },
        cancelSubmit() {
            this.showForm = false;
            this.resetForm();
        },
        resetForm() {
            this.form = {
                room_type: "",
                entity: "FCHS",
                campus: "FCHS-ALAIN",
                title: "",
                no_of_seats: null,
                max_seats: null,
                remarks: ""
            };
        },
        async handleSubmit() {
            this.isLoading = true;

            try {
                const { data } = await this.$apollo.mutate({
                    mutation: CREATE_FACILITY_ROOM_MUTATION,
                    variables: {
                        id: this.selectedId,
                        input: this.form
                    },
                });

                const response = data.createFacilityRoom;

                if (response.error) {
                    return this.$swal.fire({
                        icon: 'error',
                        title: 'Oops...',
                        text: response.message,
                    });
                }

                this.$swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: response.message,
                    showConfirmButton: false,
                    timer: 1000,
                    timerProgressBar: true,
                });

                this.loadData();

            } catch (error) {
                this.$swal.fire({
                    icon: 'error',
                    title: 'Failed!',
                    text: error.message || 'Could not create Think Tank.',
                });
            } finally {
                this.isLoading = false;
            }

            this.resetForm();
            this.showForm = false;
        },
        openFacilityPopup(module) {
            this.activeModule = module;
        },
        closeModal() {
            this.activeModule = null;
        },
        pluralize(word, count) {
            if (count === 1) return word;

            // If word ends with 'y' and is preceded by a consonant, replace 'y' with 'ies'
            if (word.endsWith('y') && !/[aeiou]y$/i.test(word)) {
                return word.slice(0, -1) + 'ies';
            }

            // Add 'es' for common endings like 's', 'x', 'z', 'ch', 'sh'
            if (/(s|x|z|ch|sh)$/i.test(word)) {
                return word + 'es';
            }

            // Default case: add 's'
            return word + 's';
        },
    },
};
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
